import { ComponentChildren } from 'preact';
import { useRef } from 'preact/hooks';
import { TabBarContext, TabBarLayout } from '../UNSAFE_TabBarCommon';
import { styles } from './themes/TabBarStyles.css';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useTabBar } from '../hooks/PRIVATE_useTabBar';
export {
  TabBarItem,
  RemovableTabBarItem,
  OverflowTabBarItem,
  TabBarContext,
  TabBarLayout
} from '../UNSAFE_TabBarCommon';

/**
 * type for payload of current key change event handler
 */
export type CurrentKeyDetail<K> = {
  value: K;
};

/**
 * type for payload of selection change event handler
 */
type SelectionDetail<K> = {
  value: K;
};

/**
 * type for payload of remove event handler
 */
type RemoveDetail<K> = {
  value: K;
};

type DisplayProps =
  | {
      /**
       * Controls the content layout of the TabBarItem: 'standard' lays out the contents horizontally,
       *'icons' hides the label,
       */
      display?: 'standard' | 'icons';
      /**
       *  The size indicates how tall the TabBarItem is rendered.
       */
      size?: 'md' | 'lg';
    }
  | {
      /**
       *'stacked' stacks the icon on top of the label.
       * Note: It is not possible to specify 'size' when 'display="stacked".
       */
      display?: 'stacked';
      size?: 'never';
    };

export type TabBarProps<K extends string | number> = DisplayProps & {
  /**
   * A set of TabBarItem(s) that TabBar will hold
   */
  children: ComponentChildren;
  /**
   * Whether to stretch the tab bar items to occupy available space or to condense items
   */
  layout?: 'stretch' | 'condense';
  /**
   * The position of the TabBar. Valid Values: top and bottom.
   * 'top' is used when TabBar is placed on top of content section and the selection
   *  indicator would be placed below the TabBarItem.
   * 'bottom' is used when TabBar is placed on bottom of content section and the selection
   *  indicator would be placed above the TabBarItem.
   */
  edge?: 'top' | 'bottom';
  /**
   * Key of the selected item.
   */
  selection?: K;
  /**
   * Callback function to handle when user selects a tab.
   * The callback is also invoked when a user selects a tab that is already selected.
   */
  onSelect?: <K extends string | number>(detail: SelectionDetail<K>) => void;
  /**
   * Callback function to handle remove
   */
  onRemove?: <K extends string | number>(detail: RemoveDetail<K>) => void;
};

export function TabBar<K extends string | number>({
  children,
  layout = 'stretch',
  display = 'standard',
  size = 'md',
  edge = 'top',
  selection,
  onSelect,
  onRemove
}: TabBarProps<K> & TestIdProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  const { rootProps, tabBarContext } = useTabBar({
    class: styles.tabBarBase,
    edge: edge,
    onRemove,
    onSelect,
    ref: rootRef,
    selection,
    size,
    children
  });
  return (
    <TabBarContext.Provider value={tabBarContext}>
      <div {...rootProps}>
        <TabBarLayout layout={layout} display={display}>
          {children}
        </TabBarLayout>
      </div>
    </TabBarContext.Provider>
  );
}
